// src/services/admins.ts
import { Entity, EntityStatistics, PlatformAdoptionStats } from "../types/entity";
import { Pagination } from "../types/pagination";
import type { RetentionReport } from "../types/retentionReport";
import type { RetentionCohort } from "../constants/retentionCohorts";
import http from "./http/index";

export interface AriaLabsPerson {
    userId: string;
    name: string;
    email: string;
    role: string;
}

const EntityAPI = {
    getEntityAdminMembershipUsers: async (params?: any) => {
        return http.get<any>("/admin/entity/entity-admin-memberships/", { params }).then((res) => res.data);
    },
    getAllEntitiesNames: async () => {
        return http.get<any>("/admin/entity/all-entities").then((res) => res.data);
    },
    getAdminsByEntityId: async (params?: any) => {
        return http.get<any>("/admin/entity/admins", { params }).then((res) => res.data);
    },
    getStandardUsersByEntityId: async (params?: any) => {
        return http.get<any>("/admin/entity/standard-users", { params }).then((res) => res.data);
    },
    getUsersByRoleAndStatus: async (params?: any) => {
        return http.get<any>("/admin/entity/users-by-role", { params }).then((res) => res.data);
    },
    getAllEntitiesAssessments: async (payload: any) => {
        return http.get<any>(
            `/admin/entity/all-entities-assessments`,
            {
                params: {
                    page: payload.page,
                    limit: payload.limit,
                    filters: payload.filters,
                    ...(payload.initialLoadLimit ? { initialLoadLimit: payload.initialLoadLimit } : {}),
                    ...(payload.skipTotalCount ? { skipTotalCount: true } : {})
                }
            }).then((res) => res.data);
    },
    assignEntityAdminMembership: async (userId: string, role?: string, targetEntityId?: string) => {
        return http.post(`/admin/entity/entity-admin-memberships/`, { userId, role }, {
            params: targetEntityId ? { entityId: targetEntityId } : undefined,
        }).then((res) => res.data);
    },
    revokeEntityAdminMembership: async (userDetailsId: string, targetEntityId?: string) => {
        return http.delete(`/admin/entity/entity-admin-memberships/${userDetailsId}`, {
            params: targetEntityId ? { entityId: targetEntityId } : undefined,
        });
    },

    archiveUserFromAdmin: async (userId: string, params?: any) => {
        return http.get(`/admin/entity/archive-user/${userId}`, {
            params
        });
    },

    getRetentionReport: async (params?: {
        start?: string;
        end?: string;
        cohort?: RetentionCohort;
    }): Promise<RetentionReport> => {
        return http
            .get<RetentionReport>("/admin/entity/retention-report", { params })
            .then((res) => res.data);
    },
    getAllEntities: async (pagination?: Pagination) => {
        return http.get<any>("/admin/entity/", {
            params: {
                ...(pagination?.page ? { page: pagination?.page } : {}),
                ...(pagination?.limit ? { limit: pagination?.limit } : {}),
                sort: pagination?.sort || 'lastActivity',
                direction: pagination?.direction || 'desc'
            }
        }).then((res) => res.data);
    },
    getAllEntitiesUsers: async (pagination?: Pagination) => {
        return http.get<any>("/admin/entity/users", {
            params: {
                ...(pagination?.page ? { page: pagination?.page } : {}),
                ...(pagination?.limit ? { limit: pagination?.limit } : {}),
                sort: pagination?.sort || 'lastActivity',
                direction: pagination?.direction || 'desc'
            }
        }).then((res) => res.data);
    },
    getEntityDetails: async (entityId: string) => {
        try {
            const response = await http.get(`/admin/entity/${entityId}`);
            // Ensure tags property exists with defaults if not set
            return {
                ...response.data,
                tags: response.data.tags || ['Employee', 'Candidate', 'Other']
            };
        } catch (error) {
            throw error;
        }
    },
    //advisors
    getAllAdvisors: async () => {
        return http.get<any>("/admin/entity/advisors").then((res) => res.data);
    },
    getEntityByToken: async (token: string) => {
        return http.get<any>(`/api/entity-links/${token}`).then((res) => res.data);
    },
    getEntityByInvitationToken: async (invitationToken: string) => {
        return http.get<any>(`/api/invitations/${invitationToken}`).then((res) => res.data);
    },
    getEntityByFeedbackLink: async (feedbackLinkUrl: string) => {
        return http.get<any>(`/api/feedback-links/${feedbackLinkUrl}`).then((res) => res.data);
    },
    validateAssessmentToken: async (token: string, diagnosticData?: any) => {
        try {
            return await http
                .get<any>(`/api/assessment-validation/validate/${token}`)
                .then((res) => res.data);
        } catch (error: any) {
            const data = error?.response?.data;
            if (data?.error) {
                return { valid: false, error: data.error, message: data.message };
            }
            throw error;
        }
    },
    validateInvitationToken: async (invitationToken: string, diagnosticData?: any) => {
        try {
            return await http
                .get<any>(`/api/assessment-validation/validate-invitation/${invitationToken}`)
                .then((res) => res.data);
        } catch (error: any) {
            const data = error?.response?.data;
            if (data?.error) {
                return { valid: false, error: data.error, message: data.message };
            }
            throw error;
        }
    },
    /** Public open positions for a Career Page Link token. */
    getCareerPagePositions: async (token: string) => {
        return http
            .get<{ positions: Array<{ positionId: string; name: string }> }>(
                `/api/assessment-validation/positions/${token}`,
            )
            .then((res) => res.data);
    },
    updateEntityDetails: async (entityId: string, entity: Partial<Entity>) => {
        return http.patch(`/admin/entity/${entityId}`, entity)
            .then((res) => res.data);
    },
    /** Saves a status change (reason / effective date) without the rest of the account form. */
    updateEntityStatus: async (entityId: string, change: Partial<Entity> & Record<string, unknown>) => {
        return http.patch(`/admin/entity/${entityId}/status`, change).then((res) => res.data);
    },
    unarchiveEntity: async (entityId: string, status: string) => {
        return http.patch(`/admin/entity/${entityId}/archive`, { status }).then((res) => res.data);
    },
    switchToEntity: async (entityId: string) => {
        return http.get(`/admin/entity/switch-to-entity/${entityId}`,).then((res) => res.data)
    },
    createNewEntity: async (entity: Partial<Entity>) => {
        return http.post("/admin/entity/", entity).then((res) => res.data);
    },
    getAllAdminUserEmails: async () => {
        return http.get<any>("/admin/entity/fetch-admin-user-emails").then((res) => res.data);
    },
    getAllRemovedAdminUserEmails: async () => {
        return http.get<any>("/admin/entity/fetch-removed-admin-user-emails").then((res) => res.data);
    },
    getAllSubscriberEmails: async () => {
        return http.get<any>("/admin/entity/fetch-subscriber-emails").then((res) => res.data);
    },
    getAllRemovedSubscriberEmails: async () => {
        return http.get<any>("/admin/entity/fetch-removed-subscriber-emails").then((res) => res.data);
    },
    getAllAdvisorEmails: async () => {
        return http.get<any>("/admin/entity/fetch-advisor-emails").then((res) => res.data);
    },
    updateDeveloperEmailPreference: async (email: string, receiveEmails: boolean) => {
        return http.patch<any>("/admin/entity/update-developer-email-preference", { email, receiveEmails }).then((res) => res.data);
    },
    getEntityStatistics: async (
        entityId?: string,
        params?: { startDate?: string; endDate?: string }
    ): Promise<EntityStatistics[]> => {
        const url = `/admin/entity/statistics${entityId ? `/${entityId}` : ''}`;
        return http.get<EntityStatistics[]>(url, { params }).then((res) => res.data);
    },
    updateEntitySettings: async (entityId: string, settings: {
        userAccessibility?: boolean;
        advisorAccessToChats?: boolean;
        tags?: string[];
        linkTags?: string[];
        integrationsEnabled?: boolean;
        integrationEmailEnabled?: boolean;
        integrationWebhooksEnabled?: boolean;
        integrationMcpOAuthEnabled?: boolean;
        integrationByokEnabled?: boolean;
        integrationSlackEnabled?: boolean;
        assessmentIntroDisplayOptions?: { _id?: string; label: string }[];
        defaultLanguage?: string;
        positionCandidatePreferencesEnabled?: boolean;
        displayUserPhotosEnabled?: boolean;
        shortAssessmentLinksEnabled?: boolean;
    }) => {
        return http.patch(`/admin/entity/${entityId}/settings`, settings)
            .then((res) => res.data);
    },
    updateSettings: async (entityId: string, settings: any) => {
        return http.patch(`/admin/entity/${entityId}/settings`, settings)
            .then((res) => res.data);
    },
    /** The org's Aria Labs include and exclude lists (active members only). */
    /** Which Aria Labs features the signed-in user has in this org. */
    getAriaLabsFeatures: async (entityId: string): Promise<Record<string, boolean>> => {
        return http.get<{ features: Record<string, boolean> }>(`/admin/entity/${entityId}/aria-labs/features`)
            .then((res) => res.data.features);
    },
    getAriaLabsPeople: async (entityId: string): Promise<{ included: AriaLabsPerson[]; excluded: AriaLabsPerson[] }> => {
        return http.get<{ included: AriaLabsPerson[]; excluded: AriaLabsPerson[] }>(`/admin/entity/${entityId}/aria-labs/people`)
            .then((res) => res.data);
    },
    /** Search the org's active members to add to an Aria Labs list. */
    searchAriaLabsCandidates: async (entityId: string, search: string): Promise<AriaLabsPerson[]> => {
        return http.get<{ people: AriaLabsPerson[] }>(`/admin/entity/${entityId}/aria-labs/candidates`, { params: { search } })
            .then((res) => res.data.people);
    },
    getClientHealth: async (entityId?: string): Promise<any> => {
        const url = `/admin/entity/health${entityId ? `?entityId=${entityId}` : ''}`;
        return http.get<any>(url).then((res) => res.data);
    },
    getPlatformAdoptionStats: async (
        entityId: string,
        dateParams?: { startDate?: string; endDate?: string }
    ): Promise<PlatformAdoptionStats> => {
        const params =
            dateParams?.startDate || dateParams?.endDate
                ? { startDate: dateParams.startDate, endDate: dateParams.endDate }
                : undefined;
        return http
            .get<PlatformAdoptionStats>(`/admin/entity/adoption-stats/${entityId}`, { params })
            .then((res) => res.data);
    },
    getActivityFeed: async (entityId: string) => {
        return http.get<any[]>(`/admin/entity/${entityId}/activity-feed`).then((res) => res.data);
    },
    setPrimaryContact: async (entityId: string, userId: string) => {
        return http.patch(`/admin/entity/${entityId}/primary-contact`, { userId }).then((res) => res.data);
    },
    sendOnboardingAgreement: async (entityId: string) => {
        return http.post(`/admin/entity/${entityId}/onboarding/send-agreement`).then((res) => res.data);
    },
    sendOnboardingInvoice: async (entityId: string) => {
        return http.post(`/admin/entity/${entityId}/onboarding/send-invoice`).then((res) => res.data);
    },
};

export default EntityAPI;